import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import ClientDataService from "../services/CustomerService";
import InfluencerBudgetService from "../services/InfluencerBudgetService";
import { ApplicationContext } from "./ApplicationContext";
import { useTable, useSortBy, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";
import { toastService } from "../services/toastService";
import { formatCurrency, getUtilizationColor } from "./utils";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

// Reusable Components
const SearchFilters = ({
  clientName,
  setClientName,
  clientCode,
  setClientCode,
  customerTypeFilter,
  setCustomerTypeFilter,
  onSearch,
  onReset,
  counts,
}) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
    {/* Customer Type Filter Tabs */}
    <div className="flex flex-wrap gap-2 mb-5 pb-5 border-b border-gray-100">
      <FilterTab
        active={customerTypeFilter === "ALL"}
        onClick={() => setCustomerTypeFilter("ALL")}
        icon="fa-users"
        label="Svi Klijenti"
        count={counts.all}
        color="gray"
      />
      <FilterTab
        active={customerTypeFilter === "COMPANY"}
        onClick={() => setCustomerTypeFilter("COMPANY")}
        icon="fa-building"
        label="Kompanije"
        count={counts.companies}
        color="blue"
      />
      <FilterTab
        active={customerTypeFilter === "INFLUENCER"}
        onClick={() => setCustomerTypeFilter("INFLUENCER")}
        icon="fa-star"
        label="Influenseri"
        count={counts.influencers}
        color="purple"
      />
    </div>

    {/* Search inputs */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1.5">
          Ime Klijenta
        </label>
        <input
          type="text"
          value={clientName}
          onChange={e => setClientName(e.target.value)}
          placeholder="Unesite ime klijenta..."
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-600 mb-1.5">
          Šifra Klijenta
        </label>
        <input
          type="text"
          value={clientCode}
          onChange={e => setClientCode(e.target.value)}
          placeholder="Unesite šifru klijenta..."
          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
        />
      </div>
      <div className="flex items-end gap-2">
        <button
          onClick={onSearch}
          className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors font-medium"
        >
          <i className="fas fa-search mr-2"></i>
          Pretraga
        </button>
        <button
          onClick={onReset}
          className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition-colors"
          title="Reset"
        >
          <i className="fas fa-undo"></i>
        </button>
      </div>
    </div>
  </div>
);

const ActionButton = ({ onClick, icon, label, variant = "primary" }) => {
  const baseClasses = "inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200";
  const variants = {
    primary: "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500",
    info: "bg-cyan-600 hover:bg-cyan-700 text-white focus:ring-cyan-500",
    warning: "bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500",
    purple: "bg-purple-600 hover:bg-purple-700 text-white focus:ring-purple-500",
  };

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variants[variant]}`}
      title={label}
    >
      <i className={`fas ${icon}`}></i>
      <span className="ml-1.5">{label}</span>
    </button>
  );
};

// Filter Tab Component for customer type filtering
const FilterTab = ({ active, onClick, icon, label, count, color }) => {
  const colorStyles = {
    gray: {
      active: "bg-gray-100 text-gray-900 border-gray-300",
      inactive: "text-gray-500 border-transparent hover:bg-gray-50 hover:text-gray-700",
    },
    blue: {
      active: "bg-blue-50 text-blue-700 border-blue-300",
      inactive: "text-gray-500 border-transparent hover:bg-blue-50 hover:text-blue-600",
    },
    purple: {
      active: "bg-purple-50 text-purple-700 border-purple-300",
      inactive: "text-gray-500 border-transparent hover:bg-purple-50 hover:text-purple-600",
    },
  };

  const style = colorStyles[color] || colorStyles.gray;

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium
        border-2 transition-all duration-200
        ${active ? style.active : style.inactive}
      `}
    >
      <i className={`fas ${icon}`}></i>
      <span>{label}</span>
      {count !== undefined && (
        <span className={`
          px-2 py-0.5 rounded-full text-xs font-semibold
          ${active
            ? (color === 'purple' ? 'bg-purple-200 text-purple-800' : color === 'blue' ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 text-gray-800')
            : 'bg-gray-100 text-gray-600'
          }
        `}>
          {count}
        </span>
      )}
    </button>
  );
};

// Dashboard Stat Card Component
const DashboardStatCard = ({ title, value, color }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-800 border-blue-200",
    orange: "bg-orange-50 text-orange-800 border-orange-200",
    purple: "bg-purple-100 text-purple-800 border-purple-300",
    gray: "bg-gray-50 text-gray-800 border-gray-200",
  };
  return (
    <div className={`rounded-lg p-4 border ${colors[color]}`}>
      <p className="text-xs font-medium mb-1 opacity-75">{title}</p>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-xs opacity-50">RSD</p>
    </div>
  );
};

// Utilization Bar Component
const UtilizationBar = ({ percentage }) => {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-200 rounded-full max-w-[80px]">
        <div className={`h-2 rounded-full ${getUtilizationColor(percentage)}`} style={{ width: `${Math.min(100, percentage)}%` }} />
      </div>
      <span className="text-xs font-medium w-10 text-right">{percentage.toFixed(0)}%</span>
    </div>
  );
};

// Influencers Dashboard Panel Component
const InfluencersDashboardPanel = ({ budgets, loading, isOpen, onToggle }) => {
  // Calculate totals
  const totals = useMemo(() => {
    return budgets.reduce((acc, b) => ({
      allocated: acc.allocated + (b.allocatedAmount || 0),
      spent: acc.spent + (b.spentAmount || 0),
      available: acc.available + (b.availableBudget || 0),
      rollover: acc.rollover + (b.rolloverAmount || 0),
    }), { allocated: 0, spent: 0, available: 0, rollover: 0 });
  }, [budgets]);

  // Chart data
  const chartData = budgets.map(b => ({
    name: b.customerName,
    Dodeljeno: b.allocatedAmount,
    Potrošeno: b.spentAmount,
  }));

  // Sorted table data (by utilization %)
  const tableData = useMemo(() => {
    return [...budgets].sort((a, b) => {
      const utilA = a.allocatedAmount > 0 ? (a.spentAmount / a.allocatedAmount) : 0;
      const utilB = b.allocatedAmount > 0 ? (b.spentAmount / b.allocatedAmount) : 0;
      return utilB - utilA;
    });
  }, [budgets]);

  return (
    <div className="bg-purple-50 border border-purple-200 rounded-xl mb-6">
      {/* Header with toggle */}
      <div
        className="flex justify-between items-center p-4 cursor-pointer hover:bg-purple-100 rounded-t-xl transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <i className="fas fa-chart-pie text-purple-600"></i>
          <h2 className="text-lg font-semibold text-purple-900">Influenser Dashboard</h2>
          <span className="bg-purple-200 text-purple-700 px-2 py-0.5 rounded-full text-xs">
            {budgets.length} influensera
          </span>
        </div>
        <i className={`fas fa-chevron-${isOpen ? "up" : "down"} text-purple-600`}></i>
      </div>

      {isOpen && (
        <div className="p-4 pt-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : budgets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <i className="fas fa-info-circle text-2xl mb-2"></i>
              <p>Nema podataka o budžetima influensera</p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <DashboardStatCard title="Ukupno Dodeljeno" value={formatCurrency(totals.allocated)} color="blue" />
                <DashboardStatCard title="Ukupno Potrošeno" value={formatCurrency(totals.spent)} color="orange" />
                <DashboardStatCard title="Ukupno Dostupno" value={formatCurrency(totals.available)} color="purple" />
                <DashboardStatCard title="Ukupan Rollover" value={formatCurrency(totals.rollover)} color="gray" />
              </div>

              {/* Comparison Chart */}
              {chartData.length > 0 && (
                <div className="bg-white rounded-lg p-4 mb-6 border border-purple-100">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Poređenje Influensera</h3>
                  <ResponsiveContainer width="100%" height={Math.max(200, budgets.length * 40)}>
                    <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(v) => `${Number(v).toLocaleString("sr-RS")} RSD`}
                        contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb" }}
                      />
                      <Legend wrapperStyle={{ fontSize: "12px" }} />
                      <Bar dataKey="Dodeljeno" fill="#c4b5fd" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="Potrošeno" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Influencer Table */}
              <div className="bg-white rounded-lg border border-purple-100 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-purple-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-purple-800 font-semibold">Influenser</th>
                        <th className="px-4 py-3 text-right text-purple-800 font-semibold">Dodeljeno</th>
                        <th className="px-4 py-3 text-right text-purple-800 font-semibold">Potrošeno</th>
                        <th className="px-4 py-3 text-right text-purple-800 font-semibold">Dostupno</th>
                        <th className="px-4 py-3 text-right text-purple-800 font-semibold">Rollover</th>
                        <th className="px-4 py-3 text-center text-purple-800 font-semibold">Iskorišćenost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.map((b, i) => {
                        const util = b.allocatedAmount > 0
                          ? (b.spentAmount / b.allocatedAmount * 100)
                          : 0;
                        return (
                          <tr key={b.customerId || i} className="border-t border-purple-50 hover:bg-purple-50 transition-colors">
                            <td className="px-4 py-3 font-medium text-gray-900">{b.customerName}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(b.allocatedAmount)}</td>
                            <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(b.spentAmount)}</td>
                            <td className="px-4 py-3 text-right text-purple-700 font-medium">
                              {formatCurrency(b.availableBudget)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500">{formatCurrency(b.rolloverAmount)}</td>
                            <td className="px-4 py-3">
                              <UtilizationBar percentage={util} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Influencer Budget Modal Component
const InfluencerBudgetModal = ({ isOpen, client, budgetData, budgetHistory, loading, onClose }) => {
  if (!isOpen) return null;

  const spentPercentage = budgetData?.allocatedAmount > 0
    ? Math.min(100, (budgetData.spentAmount / budgetData.allocatedAmount) * 100)
    : 0;

  const chartData = (budgetHistory || []).map(b => ({
    name: b.yearMonth,
    Dodeljeno: b.allocatedAmount,
    Potrošeno: b.spentAmount
  })).reverse().slice(-6);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 z-10">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Budžet Influensera</h2>
              <p className="text-purple-600 font-medium">{client?.influencerName || client?.nameOfTheLegalEntity}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
            </div>
          ) : !budgetData ? (
            <div className="text-center py-12 text-gray-500">
              <i className="fas fa-info-circle text-3xl mb-2"></i>
              <p>Nema podataka o budžetu za ovog influensera</p>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <p className="text-xs text-blue-600 font-medium mb-1">Dodeljeno</p>
                  <p className="text-lg font-bold text-blue-800">{formatCurrency(budgetData.allocatedAmount)}</p>
                  <p className="text-xs text-blue-500">RSD</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                  <p className="text-xs text-orange-600 font-medium mb-1">Potrošeno</p>
                  <p className="text-lg font-bold text-orange-800">{formatCurrency(budgetData.spentAmount)}</p>
                  <p className="text-xs text-orange-500">RSD</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                  <p className="text-xs text-purple-600 font-medium mb-1">Dostupno</p>
                  <p className="text-lg font-bold text-purple-800">{formatCurrency(budgetData.availableBudget)}</p>
                  <p className="text-xs text-purple-500">RSD</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs text-gray-600 font-medium mb-1">Rollover</p>
                  <p className="text-lg font-bold text-gray-800">{formatCurrency(budgetData.rolloverAmount)}</p>
                  <p className="text-xs text-gray-500">RSD</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-200">
                <div className="flex justify-between mb-2">
                  <span className="text-sm text-gray-600">Iskorišćenost budžeta</span>
                  <span className="text-sm font-semibold text-gray-900">{spentPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`${getUtilizationColor(spentPercentage)} h-3 rounded-full transition-all duration-500`}
                    style={{ width: `${spentPercentage}%` }}
                  />
                </div>
              </div>

              {/* Chart */}
              {chartData.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Mesečna Potrošnja</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                      <Tooltip
                        formatter={(value) => `${Number(value).toLocaleString('sr-RS')} RSD`}
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar dataKey="Dodeljeno" fill="#c4b5fd" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Potrošeno" fill="#7c3aed" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Month Info */}
              <div className="mt-4 text-center text-sm text-gray-500">
                Mesec: <span className="font-medium text-gray-700">{budgetData.yearMonth}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ClientsList = () => {
  const { userRole } = useContext(ApplicationContext);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientCode, setClientCode] = useState("");
  const [clientName, setClientName] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();

  // Customer type filter state
  const [customerTypeFilter, setCustomerTypeFilter] = useState("ALL");

  // Influencer budget modal state
  const [dashboardModal, setDashboardModal] = useState({ isOpen: false, client: null });
  const [budgetData, setBudgetData] = useState(null);
  const [budgetHistory, setBudgetHistory] = useState([]);
  const [budgetLoading, setBudgetLoading] = useState(false);

  // All influencers dashboard state
  const [showInfluencerDashboard, setShowInfluencerDashboard] = useState(true);
  const [allInfluencerBudgets, setAllInfluencerBudgets] = useState([]);
  const [allBudgetsLoading, setAllBudgetsLoading] = useState(false);

  // Calculate counts for filter tabs
  const counts = useMemo(() => ({
    all: clients.length,
    companies: clients.filter(c => c.customerType !== "INFLUENCER").length,
    influencers: clients.filter(c => c.customerType === "INFLUENCER").length,
  }), [clients]);

  // Filter clients by customer type
  const filteredClients = useMemo(() => {
    if (customerTypeFilter === "ALL") return clients;
    if (customerTypeFilter === "INFLUENCER") {
      return clients.filter(c => c.customerType === "INFLUENCER");
    }
    // COMPANY filter - show non-influencers
    return clients.filter(c => c.customerType !== "INFLUENCER");
  }, [clients, customerTypeFilter]);

  // Modal handlers
  const openDashboardModal = useCallback(async (client) => {
    setDashboardModal({ isOpen: true, client });
    setBudgetLoading(true);
    try {
      const [currentRes, historyRes] = await Promise.all([
        InfluencerBudgetService.getCurrentBudget(client.customer_id),
        InfluencerBudgetService.getBudgetHistory(client.customer_id)
      ]);
      setBudgetData(currentRes.data);
      setBudgetHistory(historyRes.data || []);
    } catch (error) {
      toastService.error("Greška pri učitavanju budžeta");
      setBudgetData(null);
      setBudgetHistory([]);
    } finally {
      setBudgetLoading(false);
    }
  }, []);

  const closeDashboardModal = useCallback(() => {
    setDashboardModal({ isOpen: false, client: null });
    setBudgetData(null);
    setBudgetHistory([]);
  }, []);

  // Load all influencer budgets for dashboard
  const loadInfluencerDashboard = useCallback(async () => {
    try {
      setAllBudgetsLoading(true);
      const res = await InfluencerBudgetService.getAllCurrentBudgets();
      setAllInfluencerBudgets(res.data || []);
    } catch (error) {
      // Silently fail - dashboard is optional
      setAllInfluencerBudgets([]);
    } finally {
      setAllBudgetsLoading(false);
    }
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await ClientDataService.getAll();
      if (Array.isArray(response.data)) {
        const sortedClients = response.data.sort((a, b) => 
          (a.isActive === b.isActive) ? 0 : a.isActive ? 1 : -1
        );
        setClients(sortedClients);
        setError(null);
      } else {
        throw new Error('Invalid data format received');
      }
    } catch (err) {
      setError("Došlo je do greške prilikom učitavanja klijenata");
      toastService.error("Greška pri učitavanju podataka");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
    if (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR") {
      loadInfluencerDashboard();
    }
  }, [loadInfluencerDashboard, userRole]);

  const handleSearch = useCallback(() => {
    const searchFilteredClients = clients.filter((client) => {
      const legalEntityName = client.nameOfTheLegalEntity?.toLowerCase() || "";
      const influencerName = client.influencerName?.toLowerCase() || "";
      const searchName = clientName.toLowerCase();
      const nameMatches = !clientName ||
        legalEntityName.includes(searchName) ||
        influencerName.includes(searchName);
      const codeMatches = !clientCode ||
        (client.customerCode && client.customerCode.toLowerCase().includes(clientCode.toLowerCase()));
      return nameMatches && codeMatches;
    });
    setClients(searchFilteredClients);
  }, [clients, clientName, clientCode]);

  const handleReset = useCallback(() => {
    setClientName("");
    setClientCode("");
    loadClients();
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "#",
        id: 'row-number',
        accessor: (row, i) => i + 1,
        Cell: ({ value }) => (
          <span className="text-gray-500 font-medium">
            {value}
          </span>
        ),
        width: 50,
        sortType: 'number'
      },
      {
        Header: "Šifra",
        accessor: "customerCode",
        width: 120,
      },
      {
        Header: "Naziv",
        accessor: "nameOfTheLegalEntity",
        Cell: ({ row }) => (
          <span>
            {row.original.customerType === 'INFLUENCER'
              ? row.original.influencerName || row.original.nameOfTheLegalEntity
              : row.original.nameOfTheLegalEntity}
          </span>
        ),
        width: 200,
      },
      {
        Header: "Tip",
        accessor: "customerType",
        Cell: ({ value }) => (
          <span className={`
            px-2 py-1 rounded-full text-xs font-medium
            ${value === 'INFLUENCER' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}
          `}>
            {value === 'INFLUENCER' ? 'Influenser' : 'Kompanija'}
          </span>
        ),
        width: 100,
      },
      {
        Header: "Grad",
        accessor: "city",
        width: 120,
      },
      {
        Header: "PIB",
        accessor: "pib",
        Cell: ({ row }) => (
          <span>
            {row.original.customerType === 'INFLUENCER'
              ? <span className="text-gray-400 text-xs">-</span>
              : row.original.pib}
          </span>
        ),
        width: 100,
      },
      {
        Header: "Status",
        accessor: "isActive",
        Cell: ({ value }) => (
          <span className={`
            px-2 py-1 rounded-full text-xs font-medium
            ${value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}
          `}>
            {value ? 'Aktivan' : 'Neaktivan'}
          </span>
        ),
        width: 100,
      },
      {
        Header: "Kontakt",
        accessor: "contactPerson",
        Cell: ({ row }) => (
          <div>
            <div>{row.original.contactPerson}</div>
            <div className="text-sm text-gray-500">{row.original.contactNumber}</div>
            <div className="text-sm text-gray-500">{row.original.email}</div>
          </div>
        ),
        width: 200,
      },
      {
        Header: "Valuta Placanja",
        accessor: "paymentCurrency",
        width: 80,
        Cell: ({ value }) => (
          <span className="text-gray-900 font-medium">
            {value || 'RSD'}
          </span>
        ),
      },
      {
        Header: "Min. Porudžbine",
        accessor: "minimumOrderAmount",
        width: 120,
        Cell: ({ value }) => (
          <span className="text-gray-900 font-medium">
            {value !== null && value !== undefined
              ? `${Number(value).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RSD`
              : <span className="text-gray-400 text-xs">Bez minimuma</span>
            }
          </span>
        ),
      },
      {
        Header: "Akcije",
        accessor: "actions",
        Cell: ({ row: { original } }) => (
          <div className="flex items-center space-x-1">
            <ActionButton
              onClick={() => navigate(`/clients/edit/${original.customer_id}`)}
              icon="fa-edit"
              label="Izmeni"
              variant="primary"
            />
            {original.customerType === 'INFLUENCER' && (
              <ActionButton
                onClick={() => openDashboardModal(original)}
                icon="fa-chart-bar"
                label="Dashboard"
                variant="purple"
              />
            )}
            {original.customerType !== 'INFLUENCER' && (
              <ActionButton
                onClick={() => navigate('/discountsOfClient', {
                  state: { clientID: original.customer_id }
                })}
                icon="fa-percent"
                label="Rabat"
                variant="info"
              />
            )}
            <ActionButton
              onClick={() => navigate('/addressesOfClient', {
                state: { clientID: original.customer_id }
              })}
              icon="fa-building"
              label="Jedinice"
              variant="warning"
            />
            <ActionButton
              onClick={() => navigate(`/customer-orders/${original.customer_id}`)}
              icon="fa-file-invoice"
              label="Porudžbine"
              variant="purple"
            />
          </div>
        ),
        width: 320,
      },
    ],
    [navigate, openDashboardModal]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize: setTablePageSize,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data: filteredClients,
      initialState: {
        pageSize,
        sortBy: [{ id: "row-number", desc: false }]
      }
    },
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">{error}</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Lista Klijenata</h1>
        <ActionButton
          onClick={() => navigate('/clients/add')}
          icon="fa-plus"
          label="Dodaj Klijenta"
          variant="primary"
        />
      </div>

      <SearchFilters
        clientName={clientName}
        setClientName={setClientName}
        clientCode={clientCode}
        setClientCode={setClientCode}
        customerTypeFilter={customerTypeFilter}
        setCustomerTypeFilter={setCustomerTypeFilter}
        onSearch={handleSearch}
        onReset={handleReset}
        counts={counts}
      />

      {/* Influencer Dashboard Panel - Only show when Influencer filter is active */}
      {customerTypeFilter === "INFLUENCER" && (
        <InfluencersDashboardPanel
          budgets={allInfluencerBudgets}
          loading={allBudgetsLoading}
          isOpen={showInfluencerDashboard}
          onToggle={() => setShowInfluencerDashboard(!showInfluencerDashboard)}
        />
      )}

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className={`
                        px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider
                        ${column.id === 'row-number' ? 'text-center' : ''}
                      `}
                      style={{ width: column.width }}
                    >
                      {column.render('Header')}
                      {column.id !== 'row-number' && column.id !== 'actions' && (
                        <span className="ml-2">
                          {column.isSorted ? (column.isSortedDesc ? '↓' : '↑') : ''}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr 
                    {...row.getRowProps()}
                    className={`
                      hover:bg-gray-50 transition-colors duration-150
                      ${!row.original.isActive ? 'bg-red-50' : ''}
                    `}
                  >
                    {row.cells.map(cell => (
                      <td
                        {...cell.getCellProps()}
                        className={`
                          px-6 py-4 whitespace-nowrap text-sm text-gray-900
                          ${cell.column.id === 'row-number' ? 'text-center' : ''}
                          ${cell.column.id === 'actions' ? 'text-right' : ''}
                        `}
                      >
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => previousPage()}
              disabled={!canPreviousPage}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Prethodna
            </button>
            <button
              onClick={() => nextPage()}
              disabled={!canNextPage}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Sledeća
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Prikazano{" "}
                <span className="font-medium">{filteredClients.length > 0 ? pageIndex * pageSize + 1 : 0}</span>
                {" "}-{" "}
                <span className="font-medium">
                  {Math.min((pageIndex + 1) * pageSize, filteredClients.length)}
                </span>
                {" "}od{" "}
                <span className="font-medium">{filteredClients.length}</span>
                {" "}rezultata
              </p>
            </div>
            <div className="flex gap-x-2">
              <select
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value));
                  setTablePageSize(Number(e.target.value));
                }}
                className="form-select rounded-md border-gray-300 text-base"
              >
                {[10, 20, 30, 40, 50].map(size => (
                  <option key={size} value={size}>
                    Prikaži {size}
                  </option>
                ))}
              </select>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Influencer Budget Modal */}
      <InfluencerBudgetModal
        isOpen={dashboardModal.isOpen}
        client={dashboardModal.client}
        budgetData={budgetData}
        budgetHistory={budgetHistory}
        loading={budgetLoading}
        onClose={closeDashboardModal}
      />
    </div>
  );
};

export default ClientsList;